/**
 * Storefront v6 primitives — the small vocabulary every section is built
 * from. Each call to action is its OWN component with its own identity (no
 * page of identical pills); badges, prices and states render only from
 * real data.
 */
import React from 'react';
import { ArrowRight, Plus, Minus } from 'lucide-react';
import { cloudinaryVariant, srcSetFor, sizesFor, FALLBACK_WIDTH } from '../data/media';

// ── Calls to action ─────────────────────────────────────────────────────
export const OrderNowButton = ({ children, className = '', arrow = true, ...rest }) => (
  <button type="button" className={`s6-btn s6-btn-order ${className}`} {...rest}>
    {children}
    {arrow && <ArrowRight className="s6-arrow w-4 h-4" aria-hidden="true" />}
  </button>
);

export const GhostButton = ({ children, className = '', ...rest }) => (
  <button type="button" className={`s6-btn s6-btn-ghost ${className}`} {...rest}>{children}</button>
);

export const BlockButton = ({ children, className = '', ...rest }) => (
  <button type="button" className={`s6-btn s6-btn-block ${className}`} {...rest}>{children}</button>
);

export const PaperButton = ({ children, className = '', ...rest }) => (
  <button type="button" className={`s6-btn s6-btn-paper ${className}`} {...rest}>{children}</button>
);

export const InlineLink = ({ children, className = '', ...rest }) => (
  <button type="button" className={`s6-btn s6-btn-inline ${className}`} {...rest}>
    {children} <ArrowRight className="w-4 h-4" aria-hidden="true" />
  </button>
);

/** The round "+" that becomes a stepper once the item is in the cart. */
export const QuickAdd = ({ qty = 0, onAdd, onRemove, label, disabled = false, testid, testidStepper }) => {
  if (qty > 0) {
    return (
      <div className="s6-stepper" data-testid={testidStepper} role="group" aria-label={label}>
        <button type="button" onClick={onRemove} aria-label={`Remove one ${label}`}><Minus className="w-4 h-4" /></button>
        <span aria-live="polite">{qty}</span>
        <button type="button" onClick={onAdd} aria-label={`Add one more ${label}`}><Plus className="w-4 h-4" /></button>
      </div>
    );
  }
  return (
    <button type="button" className="s6-quick-add" onClick={onAdd} disabled={disabled} aria-label={`Add ${label}`} data-testid={testid}>
      <Plus className="w-5 h-5" />
    </button>
  );
};

// ── Chips and badges ────────────────────────────────────────────────────
export const Chip = ({ active = false, photo, count, children, className = '', ...rest }) => (
  <button type="button" className={`s6-chip ${className}`} aria-pressed={active ? 'true' : 'false'} {...rest}>
    {photo && <img src={photo} alt="" className="s6-chip-photo" loading="lazy" />}
    {children}
    {typeof count === 'number' && <span className="s6-chip-count">{count}</span>}
  </button>
);

const BADGE_CLASS = { veg: 's6-badge-veg', vegetarian: 's6-badge-veg', vegan: 's6-badge-vegan', spicy: 's6-badge-spicy', new: 's6-badge-new', soldout: 's6-badge-soldout' };
export const Badge = ({ kind, children, className = '' }) => (
  <span className={`s6-badge ${BADGE_CLASS[kind] || ''} ${className}`}>{children}</span>
);

/** Open / closed pill from real store hours; renders nothing when unknown. */
export const StatusPill = ({ status, ink = false }) => {
  if (!status || typeof status.is_open !== 'boolean') return null;
  const text = status.is_open
    ? (status.closes_time ? `Open until ${status.closes_time}` : 'Open now')
    : (status.opens_time ? `Opens ${status.opens_time}` : 'Closed');
  return (
    <span className={`s6-status ${ink ? 's6-status-ink' : ''}`} data-state={status.is_open ? 'open' : 'closed'} data-testid="s6-status-pill">
      {text}
    </span>
  );
};

// ── Money ───────────────────────────────────────────────────────────────
export const money = (amount, currency = 'CAD') => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: String(currency || 'CAD').toUpperCase(), minimumFractionDigits: 2 }).format(n);
  } catch (_e) {
    return `$${n.toFixed(2)}`;
  }
};
export const Price = ({ amount, currency, from = false, className = '' }) => {
  const label = money(amount, currency);
  if (!label) return null;
  return <span className={`font-semibold tabular-nums ${className}`}>{from ? 'from ' : ''}{label}</span>;
};

// ── Images and states ───────────────────────────────────────────────────
/**
 * A photo with a reserved aspect ratio (no layout shift), lazy below the fold.
 * A Cloudinary photo renders a srcset (data/media owns the URL grammar) so the
 * browser fetches the rung its slot needs; `sizes` says how wide the slot is
 * (the card preset by default) and `width` picks the single-URL fallback rung.
 * Any other URL renders untouched, with no srcset.
 */
export const Photo = ({ src, alt = '', ratio = '4 / 3', eager = false, soldOut = false, sizes = sizesFor('card'), width, className = '', children }) => {
  const set = srcSetFor(src);
  const url = set ? cloudinaryVariant(src, { width: width || FALLBACK_WIDTH }) : src;
  return (
    <div className={`s6-photo ${soldOut ? 's6-photo-soldout' : ''} ${className}`} style={{ aspectRatio: ratio }}>
      {src ? <img src={url} srcSet={set || undefined} sizes={set ? sizes : undefined} alt={alt} loading={eager ? 'eager' : 'lazy'} decoding="async" /> : null}
      {children}
    </div>
  );
};

export const Skeleton = ({ ratio = '4 / 3', className = '' }) => (
  <div className={`s6-skeleton ${className}`} style={{ aspectRatio: ratio }} aria-hidden="true" />
);

export const SectionHead = ({ eyebrow, title, emphasis, sub, left = false, accentStyle = 'highlight', testid }) => (
  <div className={`s6-head ${left ? 's6-head-left' : ''}`} data-testid={testid}>
    {eyebrow && <span className="s6-eyebrow">{eyebrow}</span>}
    <h2 className="s6-h2">
      {title}{emphasis ? ' ' : ''}
      {emphasis && <span className={accentStyle === 'script' ? 's6-script s6-emphasis' : 's6-emphasis'}>{emphasis}</span>}
    </h2>
    {sub && <p className="s6-lede mx-auto">{sub}</p>}
  </div>
);

export default {
  OrderNowButton, GhostButton, BlockButton, PaperButton, InlineLink, QuickAdd,
  Chip, Badge, StatusPill, Price, money, Photo, Skeleton, SectionHead,
};
